/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../apis/axiosConfig';
import { FaEdit, FaTrash, FaCheck, FaTimes, FaExclamation } from 'react-icons/fa';
import ConfirmModal from '../common/ConfirmModal';
import './ProductManager.css';
import { validateImageFile } from '../utils/fileValidation';

const backendHost = (process.env.REACT_APP_BACKEND || 'http://localhost:8000').replace(/\/+$/, '');

const getImageUrl = (filename) => {
  if (!filename) return '';
  const clean = String(filename).replace(/^\/+/, '');
  if (clean.startsWith('http')) return clean;
  return `${backendHost}/uploads/${clean}`;
};

const isImageAttr = (attrObj, valObj) => {
  if (attrObj && String(attrObj.type) === '2') return true;
  if (valObj && String(valObj.type) === '2') return true;
  if (valObj && valObj.value && typeof valObj.value === 'string' && /\.(png|jpe?g|webp|gif|svg)$/i.test(valObj.value)) return true;
  return false;
};

const isColorAttr = (attrObj, valObj) => {
  if (attrObj && String(attrObj.type) === '1') return true;
  if (valObj && String(valObj.type) === '1') return true;
  if (valObj && valObj.value && typeof valObj.value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(valObj.value.trim())) return true;
  return false;
};

const handleImageError = (e, filename) => {
  if (!filename) return;
  const currentSrc = e.target.src || '';
  if (currentSrc.includes('aristo-india.com/backend/uploads/')) {
    e.target.src = `https://www.aristo-india.com/uploads/images/images/product-attribute/${filename}`;
  } else if (currentSrc.includes('product-attribute/')) {
    e.target.src = `https://www.aristo-india.com/uploads/${filename}`;
  } else if (!currentSrc.includes('aristo-india.com')) {
    e.target.src = `https://www.aristo-india.com/backend/uploads/${filename}`;
  }
};

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ header: [1, 2, 3, false] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['clean']
  ]
};

const quillFormats = ['bold', 'italic', 'underline', 'header', 'size', 'list', 'bullet', 'align'];

const ProductManager = () => {
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Attributes states for Product Details tab
  const [allAttributes, setAllAttributes] = useState([]);
  const [selectedAttrId, setSelectedAttrId] = useState('');
  const [selectedValuesList, setSelectedValuesList] = useState([]);
  const [selectedAssignId, setSelectedAssignId] = useState('1');
  const [tagInputVal, setTagInputVal] = useState('');
  const [showValueDropdown, setShowValueDropdown] = useState(false);
  const [valueSearchQuery, setValueSearchQuery] = useState('');
  const [showManageModal, setShowManageModal] = useState(false);
  const [managingGroup, setManagingGroup] = useState(null);
  const [deleteProductTarget, setDeleteProductTarget] = useState(null);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [editProductTarget, setEditProductTarget] = useState(null);

  const valueDropdownRef = useRef(null);

  // Edit state
  const [activeTab, setActiveTab] = useState(null);
  const [currentProduct, setCurrentProduct] = useState({
    id: null,
    category_id: '',
    sub_category_id: '',
    product: '',
    status: 1,
    search_tags: '',
    sectionContents: []
  });

  useEffect(() => {
    fetchProducts();
    fetchSections();
    fetchCategories();
    fetchAttributes();
  }, []);

  useEffect(() => {
    const handleClickOutsideValue = (e) => {
      if (valueDropdownRef.current && !valueDropdownRef.current.contains(e.target)) {
        setShowValueDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideValue);
    return () => document.removeEventListener('mousedown', handleClickOutsideValue);
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axiosInstance.get('/products');
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products', error);
    }
  };

  const fetchSections = async () => {
    try {
      const res = await axiosInstance.get('/products/sections');
      setSections(res.data);
    } catch (error) {
      console.error('Failed to fetch sections', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get('/categories');
      // Format each category to ensure 'category' field is present for JSX templates
      // Map c.parentId to c.parent_id because the JSX template filters subcategories by s.parent_id
      const formatted = res.data.map((c) => ({
        ...c,
        category: c.title || c.category,
        parent_id: c.parentId
      }));
      // Split into main and sub categories using the 'type' field from API and only allow active ones (status === true)
      const main = formatted.filter((c) => c.type === 'main' && c.status === true);
      const sub = formatted.filter((c) => c.type === 'sub' && c.status === true);
      setCategories(main);
      setSubCategories(sub);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  };

  const fetchAttributes = async () => {
    try {
      const res = await axiosInstance.get('/attributes');
      setAllAttributes(res.data);
    } catch (error) {
      console.error('Failed to fetch attributes', error);
    }
  };

  const handleCreateNew = () => {
    // Initialize empty sections based on fetched sections
    const initialContents = sections.map((sec) => {
      let parsedDefault = null;
      try {
        parsedDefault = sec.default_content ? JSON.parse(sec.default_content) : null;
      } catch (e) {
        parsedDefault = sec.default_content;
      }
      return {
        section_id: sec.id,
        content: parsedDefault || (sec.section === 'Banner Configuration' || sec.section === 'Addon Images' ? [] : {}),
        status: 1
      };
    });

    setCurrentProduct({
      id: null,
      category_id: '',
      sub_category_id: '',
      product: '',
      status: 1,
      search_tags: '',
      sectionContents: initialContents
    });
    setActiveTab(sections.length > 0 ? sections[0].id : null);
    setView('edit');
  };

  const handleEditProduct = async (id) => {
    try {
      const res = await axiosInstance.get(`/products/${id}`);
      const prod = res.data;

      // Format section contents
      const formattedContents = sections.map((sec) => {
        const existing = prod.sectionContents.find((sc) => sc.section_id === sec.id);
        if (existing) {
          let parsedContent = existing.content;
          if (typeof parsedContent === 'string') {
            try {
              parsedContent = JSON.parse(parsedContent);
            } catch (e) {
              console.error('Failed to parse existing content', e);
            }
          }
          return { ...existing, content: parsedContent };
        }

        // If new section added later
        let parsedDefault = null;
        try {
          parsedDefault = sec.default_content ? JSON.parse(sec.default_content) : null;
        } catch (e) {
          parsedDefault = sec.default_content;
        }
        return {
          section_id: sec.id,
          content: parsedDefault || (sec.section === 'Banner Configuration' || sec.section === 'Addon Images' ? [] : {}),
          status: 1
        };
      });

      setCurrentProduct({
        id: prod.id,
        category_id: prod.category_id || '',
        sub_category_id: prod.sub_category_id || '',
        product: prod.product || '',
        status: prod.status,
        search_tags: prod.search_tags || '',
        sectionContents: formattedContents
      });
      setActiveTab(sections.length > 0 ? sections[0].id : null);
      setView('edit');
    } catch (error) {
      console.error('Failed to fetch product details', error);
    }
  };

  const handleConfirmEditProduct = () => {
    if (editProductTarget) {
      const id = editProductTarget.id;
      setEditProductTarget(null);
      handleEditProduct(id);
    }
  };

  const handleConfirmDeleteProduct = async () => {
    if (!deleteProductTarget) return;
    try {
      setIsDeletingProduct(true);
      await axiosInstance.delete(`/products/${deleteProductTarget.id}`);
      fetchProducts();
      setDeleteProductTarget(null);
    } catch (error) {
      console.error('Failed to delete product', error);
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const toggleProductStatus = async (product) => {
    try {
      const newStatus = product.status === 1 ? 3 : 1;
      await axiosInstance.put(`/products/${product.id}`, { status: newStatus });
      fetchProducts();
    } catch (error) {
      console.error('Failed to toggle status', error);
    }
  };

  // Form Handle Changes
  const handleBasicInfoChange = (field, value) => {
    setCurrentProduct((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSectionContentChange = (sectionId, newContent) => {
    setCurrentProduct((prev) => {
      const updatedContents = prev.sectionContents.map((sc) => {
        if (sc.section_id === sectionId) {
          return { ...sc, content: newContent };
        }
        return sc;
      });
      return { ...prev, sectionContents: updatedContents };
    });
  };

  const toggleSectionStatus = (sectionId) => {
    setCurrentProduct((prev) => {
      const updatedContents = prev.sectionContents.map((sc) => {
        if (sc.section_id === sectionId) {
          return { ...sc, status: sc.status === 1 ? 3 : 1 };
        }
        return sc;
      });
      return { ...prev, sectionContents: updatedContents };
    });
  };

  const enableSection = (sectionId) => {
    setCurrentProduct((prev) => {
      const updatedContents = prev.sectionContents.map((sc) => {
        if (sc.section_id === sectionId) {
          return { ...sc, status: 1 };
        }
        return sc;
      });
      return { ...prev, sectionContents: updatedContents };
    });
  };

  // Image Upload
  const handleImageUpload = async (e, sectionId, index, field = 'image') => {
    const file = e.target.files[0];
    if (!validateImageFile(file, e)) return;
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axiosInstance.post('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = res.data.filename;

      // Update array content
      const sc = currentProduct.sectionContents.find((s) => s.section_id === sectionId);
      const contentArray = [...(sc.content || [])];
      if (contentArray[index]) {
        contentArray[index] = { ...contentArray[index], [field]: imageUrl };
        handleSectionContentChange(sectionId, contentArray);
      }
    } catch (error) {
      console.error('Upload failed', error);
    }
  };

  const addSlide = (sectionId) => {
    const sc = currentProduct.sectionContents.find((s) => s.section_id === sectionId);
    const contentArray = [...(sc.content || [])];
    contentArray.push({
      id: Date.now().toString(),
      image: '',
      heading: '',
      description: '',
      title: '',
      status: '1',
      sort: contentArray.length + 1
    });
    handleSectionContentChange(sectionId, contentArray);
  };

  const removeSlide = (sectionId, index) => {
    const sc = currentProduct.sectionContents.find((s) => s.section_id === sectionId);
    const contentArray = [...(sc.content || [])];
    contentArray.splice(index, 1);
    handleSectionContentChange(sectionId, contentArray);
  };

  const updateSlideField = (sectionId, index, field, value) => {
    const sc = currentProduct.sectionContents.find((s) => s.section_id === sectionId);
    const contentArray = [...(sc.content || [])];
    contentArray[index] = { ...contentArray[index], [field]: value };
    handleSectionContentChange(sectionId, contentArray);
  };

  // Product Details handlers
  const handleValueCheckboxChange = (valId) => {
    const stringId = String(valId);
    setSelectedValuesList((prev) => (prev.includes(stringId) ? prev.filter((id) => id !== stringId) : [...prev, stringId]));
  };

  const handleAddDetailItem = () => {
    if (!selectedAttrId || selectedValuesList.length === 0 || !selectedAssignId) {
      alert('Please select Attribute, Value(s) and Assign to.');
      return;
    }

    const selectedAttr = allAttributes.find((a) => String(a.id) === String(selectedAttrId));
    if (!selectedAttr) return;

    // Get current details object
    const sc = currentProduct.sectionContents.find((s) => s.section_id === activeTab);
    const detailsObj = { ...(sc?.content || {}) };

    // Add each selected value
    selectedValuesList.forEach((valId) => {
      const itemKey = `${selectedAttrId}${valId}`;
      detailsObj[itemKey] = {
        id: itemKey,
        assign_to: String(selectedAssignId),
        attr_id: String(selectedAttrId),
        attr_val: String(valId),
        status: 1
      };
    });

    handleSectionContentChange(activeTab, detailsObj);

    // Reset selections
    setSelectedAttrId('');
    setSelectedValuesList([]);
    setValueSearchQuery('');
    setShowValueDropdown(false);
  };

  const handleOpenManageModal = (group) => {
    setManagingGroup(group);
    setShowManageModal(true);
  };

  const handleRemoveValueFromGroup = (valId) => {
    if (!managingGroup) return;

    const itemKey = `${managingGroup.attr_id}${valId}`;

    // Remove from product state
    const sc = currentProduct.sectionContents.find((s) => s.section_id === activeTab);
    const detailsObj = { ...(sc?.content || {}) };
    delete detailsObj[itemKey];
    handleSectionContentChange(activeTab, detailsObj);

    // Update local modal state
    const updatedValues = managingGroup.values.filter((v) => String(v) !== String(valId));
    const updatedKeys = managingGroup.itemKeys.filter((k) => k !== itemKey);

    if (updatedValues.length === 0) {
      // Close modal if no elements remain
      setShowManageModal(false);
      setManagingGroup(null);
    } else {
      setManagingGroup({
        ...managingGroup,
        values: updatedValues,
        itemKeys: updatedKeys
      });
    }
  };

  const handleSearchTagsChange = (val) => {
    setCurrentProduct((prev) => ({
      ...prev,
      search_tags: val
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const value = tagInputVal.replace(/,/g, '').trim();
      if (value) {
        const currentTags = (currentProduct.search_tags || '')
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t !== '');
        if (!currentTags.includes(value)) {
          const updated = [...currentTags, value].join(',');
          handleSearchTagsChange(updated);
        }
      }
      setTagInputVal('');
    } else if (e.key === 'Backspace' && !tagInputVal) {
      const currentTags = (currentProduct.search_tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '');
      if (currentTags.length > 0) {
        currentTags.pop();
        handleSearchTagsChange(currentTags.join(','));
      }
    }
  };

  const handleTagInputBlur = () => {
    const value = tagInputVal.trim();
    if (value) {
      const currentTags = (currentProduct.search_tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '');
      if (!currentTags.includes(value)) {
        const updated = [...currentTags, value].join(',');
        handleSearchTagsChange(updated);
      }
    }
    setTagInputVal('');
  };

  const handleRemoveTag = (indexToRemove) => {
    const currentTags = (currentProduct.search_tags || '')
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');
    const updatedTags = currentTags.filter((_, idx) => idx !== indexToRemove);
    handleSearchTagsChange(updatedTags.join(','));
  };

  // Save Product
  const handleSave = async () => {
    if (!currentProduct.category_id || !currentProduct.sub_category_id || !currentProduct.product) {
      alert('Please fill out Category, Sub Category and Product Code in Basic Info.');
      return;
    }

    try {
      if (currentProduct.id) {
        await axiosInstance.put(`/products/${currentProduct.id}`, currentProduct);
      } else {
        await axiosInstance.post('/products', currentProduct);
      }
      setView('list');
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product', error);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND || 'http://localhost:8000';
  const cleanHost = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;

  // Render logic for different sections
  const renderSectionContent = () => {
    const activeSection = sections.find((s) => s.id === activeTab);
    if (!activeSection) return null;

    const currentSc = currentProduct.sectionContents.find((sc) => sc.section_id === activeTab);
    const isActive = currentSc && currentSc.status === 1;

    if (!isActive && activeSection.section !== 'Basic Info') {
      return (
        <div className="disabled-overlay">
          <h2>Oops!</h2>
          <p>Please enable or activate {activeSection.section} to modify the content.</p>
          <button className="enable-btn" onClick={() => enableSection(activeTab)}>
            Enable / Activate
          </button>
        </div>
      );
    }

    if (activeSection.section === 'Basic Info') {
      return (
        <div className="basic-info-grid">
          <div className="pm-input-group">
            <label>
              Category <span>*</span>
            </label>
            <select value={currentProduct.category_id} onChange={(e) => handleBasicInfoChange('category_id', e.target.value)}>
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.category}
                </option>
              ))}
            </select>
          </div>
          <div className="pm-input-group">
            <label>
              Sub Category <span>*</span>
            </label>
            <select value={currentProduct.sub_category_id} onChange={(e) => handleBasicInfoChange('sub_category_id', e.target.value)}>
              <option value="">Select Sub Category</option>
              {subCategories
                .filter((s) => (currentProduct.category_id ? String(s.parent_id) === String(currentProduct.category_id) : true))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.category}
                  </option>
                ))}
            </select>
          </div>
          <div className="pm-input-group full-width">
            <label>
              Product Code <span>*</span>
            </label>
            <input
              type="text"
              value={currentProduct.product}
              onChange={(e) => handleBasicInfoChange('product', e.target.value)}
              placeholder="Enter Product Code (e.g. ALMA)"
            />
          </div>
        </div>
      );
    }

    if (activeSection.section === 'Banner Configuration' || activeSection.section === 'Addon Images') {
      const slides = Array.isArray(currentSc?.content) ? currentSc.content : [];

      return (
        <div>
          {slides.map((slide, index) => (
            <div key={index} className="slide-block">
              <div className="slide-header">
                <div className="slide-title">Slide {index + 1}</div>
                <div className="slide-actions">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={slide.status !== '0'}
                      onChange={(e) => updateSlideField(activeTab, index, 'status', e.target.checked ? '1' : '0')}
                    />
                    <span className="slider active-blue"></span>
                  </label>
                  <i className="bi bi-trash" style={{ cursor: 'pointer', color: '#999' }} onClick={() => removeSlide(activeTab, index)}></i>
                </div>
              </div>
              <div className="slide-content-grid">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                    Product Image <span>*</span>
                  </label>
                  <div className="image-upload-box" onClick={() => document.getElementById(`upload-${activeTab}-${index}`).click()}>
                    {slide.image ? (
                      <img src={`${cleanHost}/uploads/${slide.image}`} alt="Preview" />
                    ) : (
                      <i className="bi bi-image" style={{ fontSize: '40px', color: '#ccc' }}></i>
                    )}
                    <div className="upload-overlay">
                      <i className="bi bi-upload"></i>
                    </div>
                    <input
                      type="file"
                      id={`upload-${activeTab}-${index}`}
                      style={{ display: 'none' }}
                      onChange={(e) => handleImageUpload(e, activeTab, index)}
                    />
                  </div>
                  <div className="image-hint">Image with dimensions 800 x 500 px and max size 1.5MB</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div className="pm-input-group" style={{ marginBottom: 0 }}>
                    <label>
                      Product Image Title <span style={{ color: '#888', fontWeight: 'normal' }}>(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={slide.title || slide.heading || ''}
                      onChange={(e) => updateSlideField(activeTab, index, 'title', e.target.value)}
                      placeholder="Product Image Title"
                    />
                  </div>
                  <div className="pm-input-group" style={{ marginBottom: 0, flexGrow: 1 }}>
                    <label>
                      Product Image Description <span style={{ color: '#888', fontWeight: 'normal' }}>(Optional)</span>
                    </label>
                    <textarea
                      style={{ flexGrow: 1, resize: 'none' }}
                      value={slide.description || ''}
                      onChange={(e) => updateSlideField(activeTab, index, 'description', e.target.value)}
                      placeholder="Product Image Description"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button className="add-slide-btn" onClick={() => addSlide(activeTab)}>
            <i className="bi bi-plus-lg"></i> Add New Slide
          </button>
        </div>
      );
    }

    if (activeSection.section === 'SEO Manager') {
      const seo =
        typeof currentSc?.content === 'object' && currentSc?.content !== null && !Array.isArray(currentSc?.content)
          ? currentSc.content
          : {};
      return (
        <div className="basic-info-grid">
          <div className="pm-input-group full-width">
            <label>Meta Title</label>
            <input
              type="text"
              value={seo.meta_title || ''}
              onChange={(e) => handleSectionContentChange(activeTab, { ...seo, meta_title: e.target.value })}
              placeholder="Meta Title"
            />
          </div>
          <div className="pm-input-group full-width">
            <label>Meta Keywords</label>
            <input
              type="text"
              value={seo.meta_keywords || ''}
              onChange={(e) => handleSectionContentChange(activeTab, { ...seo, meta_keywords: e.target.value })}
              placeholder="Meta Keywords"
            />
          </div>
          <div className="pm-input-group full-width">
            <label>Meta Description</label>
            <textarea
              value={seo.meta_description || ''}
              onChange={(e) => handleSectionContentChange(activeTab, { ...seo, meta_description: e.target.value })}
              rows="4"
              placeholder="Meta Description"
            ></textarea>
          </div>
        </div>
      );
    }

    // Product Details array
    if (activeSection.section === 'Product Details') {
      const detailsObj = typeof currentSc?.content === 'object' && currentSc?.content !== null ? currentSc.content : {};
      const items = Object.values(detailsObj);

      // Group items by attr_id + '_' + assign_to
      const groupedDetails = {};
      items.forEach((item) => {
        const groupKey = `${item.attr_id}_${item.assign_to}`;
        if (!groupedDetails[groupKey]) {
          groupedDetails[groupKey] = {
            attr_id: item.attr_id,
            assign_to: item.assign_to,
            itemKeys: [],
            values: []
          };
        }
        groupedDetails[groupKey].itemKeys.push(item.id);
        groupedDetails[groupKey].values.push(item.attr_val);
      });
      const groupedList = Object.values(groupedDetails);

      const selectedAttr = allAttributes.find((a) => String(a.id) === String(selectedAttrId));
      const attrValues = selectedAttr ? selectedAttr.value || [] : [];

      // Filter values based on search query
      const filteredAttrValues = attrValues.filter((val) =>
        (val.label || val.value || '').toLowerCase().includes(valueSearchQuery.toLowerCase())
      );

      return (
        <div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            {/* Attribute Dropdown */}
            <div className="pm-input-group" style={{ marginBottom: 0, flex: 1, minWidth: '180px' }}>
              <label>
                Attribute <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={selectedAttrId}
                onChange={(e) => {
                  setSelectedAttrId(e.target.value);
                  setSelectedValuesList([]);
                  setValueSearchQuery('');
                  setShowValueDropdown(false);
                }}
                style={{ width: '100%', padding: '0.65rem 0.85rem' }}
              >
                <option value="">Select Attribute</option>
                {allAttributes
                  .filter(
                    (attr) =>
                      attr.status === 1 &&
                      (!currentProduct.category_id ||
                        (Array.isArray(attr.assign_category) &&
                          (attr.assign_category.map(String).includes(String(currentProduct.category_id)) ||
                            attr.assign_category.map(String).includes(String(currentProduct.sub_category_id)))))
                  )
                  .map((attr) => (
                    <option key={attr.id} value={attr.id}>
                      {attr.attribute}
                    </option>
                  ))}
              </select>
            </div>

            {/* Custom Value Multi-Select Dropdown */}
            <div
              className="pm-input-group"
              style={{ marginBottom: 0, flex: 1, minWidth: '180px', position: 'relative' }}
              ref={valueDropdownRef}
            >
              <label>
                Value <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <button
                type="button"
                disabled={!selectedAttrId}
                onClick={() => setShowValueDropdown(!showValueDropdown)}
                style={{
                  width: '100%',
                  padding: '0.65rem 0.85rem',
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(196, 168, 130, 0.2)',
                  borderRadius: '8px',
                  color: !selectedAttrId ? 'rgba(245, 240, 232, 0.25)' : '#f5f0e8',
                  fontSize: '13px',
                  textAlign: 'left',
                  cursor: !selectedAttrId ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '42px',
                  boxSizing: 'border-box'
                }}
              >
                <span>{selectedValuesList.length > 0 ? `Selected (${selectedValuesList.length})` : 'Select Options'}</span>
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>

              {showValueDropdown && selectedAttrId && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#151515',
                    border: '1px solid rgba(196, 168, 130, 0.25)',
                    borderRadius: '8px',
                    boxShadow: '0 6px 15px rgba(0,0,0,0.5)',
                    padding: '10px',
                    zIndex: 100,
                    marginTop: '5px',
                    maxHeight: '260px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Search Field */}
                  <input
                    type="text"
                    placeholder="Search..."
                    value={valueSearchQuery}
                    onChange={(e) => setValueSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '0.4rem 0.75rem',
                      fontSize: '13px',
                      background: 'rgba(0, 0, 0, 0.25)',
                      border: '1px solid rgba(196, 168, 130, 0.2)',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      color: '#f5f0e8',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  {/* Options List */}
                  <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {filteredAttrValues.length === 0 ? (
                      <div style={{ padding: '8px', fontSize: '12px', color: 'rgba(245,240,232,0.4)', textAlign: 'center' }}>
                        No values found
                      </div>
                    ) : (
                      filteredAttrValues.map((val, idx) => {
                        const valId = String(val.id !== undefined ? val.id : idx);
                        const isChecked = selectedValuesList.includes(valId);
                        return (
                          <label
                            key={valId}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              margin: 0,
                              background: isChecked ? 'rgba(196, 168, 130, 0.08)' : 'transparent',
                              transition: 'background 0.2s',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(196,168,130,0.12)')}
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = isChecked ? 'rgba(196, 168, 130, 0.08)' : 'transparent')
                            }
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleValueCheckboxChange(valId)}
                              style={{ width: '15px', height: '15px', margin: 0, cursor: 'pointer' }}
                            />
                            {/* Thumbnail Preview */}
                            {isColorAttr(selectedAttr, val) && val.value && (
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  background: val.value,
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  flexShrink: 0
                                }}
                              />
                            )}
                            {isImageAttr(selectedAttr, val) && val.value && (
                              <img
                                src={getImageUrl(val.value)}
                                alt="preview"
                                style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                                onError={(e) => handleImageError(e, val.value)}
                              />
                            )}
                            <span style={{ fontSize: '13px', color: 'rgba(245, 240, 232, 0.85)' }}>{val.label || val.value}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Assign to Dropdown */}
            <div className="pm-input-group" style={{ marginBottom: 0, flex: 1, minWidth: '180px' }}>
              <label>
                Assign to <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={selectedAssignId}
                onChange={(e) => setSelectedAssignId(e.target.value)}
                style={{ width: '100%', padding: '0.65rem 0.85rem' }}
              >
                <option value="1">Product Details</option>
                <option value="2">Profile</option>
              </select>
            </div>

            <button
              type="button"
              onClick={handleAddDetailItem}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#c4a882',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                padding: '0.75rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'color 0.2s',
                minWidth: '90px'
              }}
            >
              + Add to list
            </button>
          </div>

          {/* Grouped details list table */}
          <div
            style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(196, 168, 130, 0.15)',
              borderRadius: '12px'
            }}
          >
            <h3
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#c4a882',
                marginBottom: '1.25rem',
                borderBottom: '1px solid rgba(196, 168, 130, 0.15)',
                paddingBottom: '0.5rem'
              }}
            >
              Attribute & Value List
            </h3>
            {groupedList.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(245,240,232,0.4)', fontSize: '13px' }}>
                No attributes added yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid rgba(196,168,130,0.25)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 15px', color: '#c4a882', fontSize: '13px', fontWeight: '600' }}>
                        Assign to
                      </th>
                      <th style={{ textAlign: 'left', padding: '12px 15px', color: '#c4a882', fontSize: '13px', fontWeight: '600' }}>
                        Attribute
                      </th>
                      <th style={{ textAlign: 'left', padding: '12px 15px', color: '#c4a882', fontSize: '13px', fontWeight: '600' }}>
                        Value
                      </th>
                      <th
                        style={{
                          width: '80px',
                          textAlign: 'center',
                          padding: '12px 15px',
                          color: '#c4a882',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedList.map((group, gIdx) => {
                      const attr = allAttributes.find((a) => String(a.id) === String(group.attr_id));
                      const groupName = group.assign_to === '2' ? 'Profile' : 'Product Details';

                      return (
                        <tr key={gIdx} style={{ borderBottom: '1px solid rgba(196,168,130,0.1)' }}>
                          <td style={{ padding: '12px 15px', fontSize: '13px', color: 'rgba(245, 240, 232, 0.85)', fontWeight: '500' }}>
                            {groupName}
                          </td>
                          <td style={{ padding: '12px 15px', fontSize: '13px', color: 'rgba(245, 240, 232, 0.85)' }}>
                            {attr ? attr.attribute : `Attr #${group.attr_id}`}
                          </td>
                          <td style={{ padding: '12px 15px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {group.values.map((valId, vIdx) => {
                                const valItem = attr
                                  ? (attr.value || []).find((v, idx) => String(v.id !== undefined ? v.id : idx) === String(valId))
                                  : null;
                                if (!valItem) return null;

                                return (
                                  <div
                                    key={vIdx}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '4px 8px',
                                      background: 'rgba(255, 255, 255, 0.02)',
                                      border: '1px solid rgba(196, 168, 130, 0.2)',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      color: 'rgba(245, 240, 232, 0.9)'
                                    }}
                                  >
                                    {isColorAttr(attr, valItem) && valItem.value && (
                                      <div
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '3px',
                                          background: valItem.value,
                                          border: '1px solid rgba(255,255,255,0.15)'
                                        }}
                                      />
                                    )}
                                    {isImageAttr(attr, valItem) && valItem.value && (
                                      <img
                                        src={getImageUrl(valItem.value)}
                                        alt="preview"
                                        style={{ width: '20px', height: '20px', borderRadius: '3px', objectFit: 'cover' }}
                                        onError={(e) => handleImageError(e, valItem.value)}
                                      />
                                    )}
                                    <span>{valItem.label || valItem.value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center', padding: '12px 15px' }}>
                            <button
                              type="button"
                              onClick={() => handleOpenManageModal(group)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#c4a882',
                                cursor: 'pointer',
                                fontSize: '15px',
                                padding: 0
                              }}
                            >
                              <FaEdit />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Search Product Tags Card */}
          <div
            style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(196, 168, 130, 0.15)',
              borderRadius: '12px'
            }}
          >
            <h3
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#c4a882',
                marginBottom: '1.25rem',
                borderBottom: '1px solid rgba(196, 168, 130, 0.15)',
                paddingBottom: '0.5rem'
              }}
            >
              Search Product Tags
            </h3>
            <div className="pm-input-group full-width" style={{ marginBottom: 0 }}>
              <label>
                Search Tags <span style={{ color: '#ef4444' }}>*</span>
              </label>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  border: '1px solid rgba(196, 168, 130, 0.2)',
                  borderRadius: '8px',
                  background: 'rgba(0, 0, 0, 0.2)',
                  minHeight: '44px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {/* Display Tags as Boxes */}
                {(currentProduct.search_tags || '')
                  .split(',')
                  .map((t) => t.trim())
                  .filter((t) => t !== '')
                  .map((tag, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'rgba(196, 168, 130, 0.1)',
                        border: '1px solid rgba(196, 168, 130, 0.3)',
                        borderRadius: '6px',
                        color: '#c4a882',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(245, 240, 232, 0.5)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: 1
                        }}
                        onMouseEnter={(e) => (e.target.style.color = '#ef4444')}
                        onMouseLeave={(e) => (e.target.style.color = 'rgba(245, 240, 232, 0.5)')}
                      >
                        &times;
                      </button>
                    </div>
                  ))}

                {/* Input element */}
                <input
                  type="text"
                  value={tagInputVal}
                  onChange={(e) => setTagInputVal(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  onBlur={handleTagInputBlur}
                  placeholder={!(currentProduct.search_tags || '').trim() ? 'Enter tags...' : ''}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: '#f5f0e8',
                    fontSize: '13px',
                    padding: '4px 0',
                    boxShadow: 'none'
                  }}
                />
              </div>
              <span style={{ fontSize: '11px', color: 'rgba(245, 240, 232, 0.45)', display: 'block', marginTop: '6px' }}>
                Add commas to separate the Search tags
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (activeSection.section === 'Add Description' || activeSection.section === 'Product Description' || Number(activeSection.id) === 5) {
      const descObj =
        typeof currentSc?.content === 'object' && currentSc?.content !== null && !Array.isArray(currentSc?.content)
          ? currentSc.content
          : { html: typeof currentSc?.content === 'string' ? currentSc.content : '' };
      const htmlValue = descObj.html || '';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div className="quill-editor-wrapper">
            <ReactQuill
              theme="snow"
              value={htmlValue}
              onChange={(content) => handleSectionContentChange(activeTab, { ...descObj, html: content, status: 1 })}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Write product description here..."
            />
          </div>
        </div>
      );
    }

    if (activeSection.section === 'Key Features' || activeSection.section === 'Key Feature' || Number(activeSection.id) === 6) {
      let featuresList = [];
      if (Array.isArray(currentSc?.content)) {
        featuresList = currentSc.content;
      } else if (currentSc?.content && typeof currentSc.content === 'object' && Array.isArray(currentSc.content.features)) {
        featuresList = currentSc.content.features;
      } else if (typeof currentSc?.content === 'string') {
        try {
          const p = JSON.parse(currentSc.content);
          featuresList = Array.isArray(p) ? p : p?.features || [];
        } catch (e) {}
      }

      const normalizedList = featuresList.map((item) => {
        if (typeof item === 'string') return { title: item, desc: '' };
        return item || { title: '', desc: '' };
      });

      const handleAddFeature = () => {
        const nextList = [...normalizedList, { title: '', desc: '' }];
        handleSectionContentChange(activeTab, nextList);
      };

      const handleUpdateFeature = (index, field, val) => {
        const nextList = normalizedList.map((item, i) => {
          if (i === index) {
            return { ...item, [field]: val };
          }
          return item;
        });
        handleSectionContentChange(activeTab, nextList);
      };

      const handleRemoveFeature = (index) => {
        const nextList = normalizedList.filter((_, i) => i !== index);
        handleSectionContentChange(activeTab, nextList);
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <h4 style={{ color: '#f5f0e8', margin: 0, fontSize: '16px', fontWeight: 600 }}>Key Feature Highlights</h4>
              <span style={{ fontSize: '12px', color: 'rgba(245, 240, 232, 0.55)' }}>
                Add key feature bullet points (e.g. 5 points) that will be displayed in the Key Features section on product pages.
              </span>
            </div>
            <button
              type="button"
              className="pm-btn pm-btn-primary"
              onClick={handleAddFeature}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                fontSize: '12px',
                background: '#c4a882',
                color: '#000',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              + Add Feature Point
            </button>
          </div>

          {normalizedList.length === 0 ? (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                border: '1px dashed rgba(245, 240, 232, 0.2)',
                borderRadius: '10px',
                color: 'rgba(245, 240, 232, 0.6)',
                background: 'rgba(255, 255, 255, 0.01)'
              }}
            >
              <i className="bi bi-stars" style={{ fontSize: '32px', display: 'block', marginBottom: '10px', color: '#c4a882' }}></i>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>No key features added yet for this product.</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'rgba(245, 240, 232, 0.4)' }}>
                Click the button below to add your first key feature point.
              </p>
              <button
                type="button"
                className="pm-btn pm-btn-secondary"
                onClick={handleAddFeature}
                style={{
                  marginTop: '14px',
                  fontSize: '12px',
                  padding: '7px 18px',
                  background: 'rgba(196, 168, 130, 0.15)',
                  color: '#c4a882',
                  border: '1px solid rgba(196, 168, 130, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                + Add First Key Feature
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {normalizedList.map((feat, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(196, 168, 130, 0.15)',
                    borderRadius: '8px'
                  }}
                >
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'rgba(196, 168, 130, 0.15)',
                      color: '#c4a882',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '12px',
                      flexShrink: 0
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      className="pm-input"
                      placeholder={`Key Feature ${idx + 1} (e.g. 100% Moisture Proof / German Precision Hardware)`}
                      value={feat.title || ''}
                      onChange={(e) => handleUpdateFeature(idx, 'title', e.target.value)}
                      style={{
                        width: '100%',
                        fontSize: '13px',
                        padding: '9px 12px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(196, 168, 130, 0.2)',
                        borderRadius: '6px',
                        color: '#fff',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    title="Delete feature point"
                    onClick={() => handleRemoveFeature(idx)}
                    style={{
                      padding: '8px 12px',
                      color: '#ff5c5c',
                      background: 'rgba(255, 92, 92, 0.1)',
                      border: '1px solid rgba(255, 92, 92, 0.2)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <div>Configuration for {activeSection.section}</div>;
  };

  return (
    <div className="premium-page-container">
      {view === 'list' &&
        (() => {
          const filteredProducts = products.filter((p) => {
            const term = searchTerm.toLowerCase();
            return (
              (p.product && p.product.toLowerCase().includes(term)) ||
              (p.mainCategory?.category && p.mainCategory.category.toLowerCase().includes(term)) ||
              (p.subCategory?.category && p.subCategory.category.toLowerCase().includes(term))
            );
          });

          const indexOfLastItem = currentPage * itemsPerPage;
          const indexOfFirstItem = indexOfLastItem - itemsPerPage;
          const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

          return (
            <>
              <div className="premium-page-header">
                <h2 className="page-title">Product Manager</h2>

                <div className="pm-search search-box" style={{ margin: '0 auto' }}>
                  <i className="bi bi-search search-icon"></i>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search here"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <button className="pm-create-btn" onClick={handleCreateNew}>
                    <i className="bi bi-plus-lg"></i> Create
                  </button>
                </div>
              </div>
              <div className="premium-table-wrapper">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th style={{ width: '4%' }}>
                        <input type="checkbox" className="premium-checkbox" />
                      </th>
                      <th style={{ width: '12%' }}>Status</th>
                      <th style={{ width: '24%' }}>Product Code</th>
                      <th style={{ width: '12%' }}>Brand</th>
                      <th style={{ width: '20%' }}>Category</th>
                      <th style={{ width: '18%' }}>Created on</th>
                      <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <input type="checkbox" className="premium-checkbox" />
                        </td>
                        <td>
                          <div className="switch-container">
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label className="switch" htmlFor={`status-toggle-${p.id}`}>
                              <span
                                style={{
                                  position: 'absolute',
                                  width: '1px',
                                  height: '1px',
                                  padding: 0,
                                  margin: '-1px',
                                  overflow: 'hidden',
                                  clip: 'rect(0, 0, 0, 0)',
                                  border: 0
                                }}
                              >
                                Toggle Status
                              </span>
                              <input
                                id={`status-toggle-${p.id}`}
                                type="checkbox"
                                checked={p.status === 1}
                                onChange={() => toggleProductStatus(p)}
                              />
                              <span className="slider active-blue"></span>
                            </label>
                            <span className="switch-label">{p.status === 1 ? 'Active' : 'Inactive'}</span>
                          </div>
                        </td>
                        <td style={{ fontWeight: '600', fontSize: '12.5px', color: '#f5f0e8' }}>{p.product}</td>
                        <td style={{ fontSize: '12px', color: '#a0a0a0' }}>Aristo India</td>
                        <td>
                          <div className="category-stacked">
                            <span className="category-main">{p.mainCategory?.category || '-'}</span>
                            <span className="category-sub">{p.subCategory?.category || '-'}</span>
                          </div>
                        </td>
                        <td className="nowrap-cell" style={{ fontSize: '12px' }}>
                          {new Date(p.created_on).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div className="inline-actions-container" style={{ justifyContent: 'center' }}>
                            <button className="btn-inline-action edit-action" onClick={() => setEditProductTarget(p)} title="Edit">
                              <FaEdit size={13} />
                            </button>
                            <button className="btn-inline-action delete-action" onClick={() => setDeleteProductTarget(p)} title="Delete">
                              <FaTrash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {filteredProducts.length > 0 && (
                <div className="premium-pagination-container">
                  <div className="premium-pagination-info">
                    Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredProducts.length)}</span> of{' '}
                    <span>{filteredProducts.length}</span> entries
                  </div>
                  <div className="premium-pagination-list">
                    <button className="premium-pagination-btn arrow-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                      &laquo;
                    </button>
                    <button
                      className="premium-pagination-btn arrow-btn"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      &lsaquo;
                    </button>

                    {Array.from({ length: Math.ceil(filteredProducts.length / itemsPerPage) }, (_, idx) => idx + 1)
                      .filter((page) => {
                        const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => {
                        return (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                            )}
                            <button
                              className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}

                    <button
                      className="premium-pagination-btn arrow-btn"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredProducts.length / itemsPerPage)))}
                      disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)}
                    >
                      &rsaquo;
                    </button>
                    <button
                      className="premium-pagination-btn arrow-btn"
                      onClick={() => setCurrentPage(Math.ceil(filteredProducts.length / itemsPerPage))}
                      disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              )}
            </>
          );
        })()}

      {view === 'edit' && (
        <>
          <h2 className="pm-title" style={{ marginBottom: '20px' }}>
            Product Manager
          </h2>
          <div className="pm-form-layout">
            <div className="pm-sidebar">
              {sections.map((sec) => {
                const currentSc = currentProduct.sectionContents.find((sc) => sc.section_id === sec.id);
                const isActive = currentSc ? currentSc.status === 1 : false;

                let statusIcon = (
                  <div className="pm-status-icon pm-success">
                    <FaCheck size={12} />
                  </div>
                );
                if (!isActive && sec.section !== 'Basic Info') {
                  statusIcon = (
                    <div className="pm-status-icon pm-error">
                      <FaTimes size={12} />
                    </div>
                  );
                }
                if (sec.section === 'SEO Manager' && !isActive) {
                  statusIcon = (
                    <div className="pm-status-icon pm-warning">
                      <FaExclamation size={12} />
                    </div>
                  );
                }

                return (
                  <div key={sec.id} className={`pm-tab ${activeTab === sec.id ? 'active' : ''}`} onClick={() => setActiveTab(sec.id)}>
                    <div className="pm-tab-left">
                      <i className={`bi ${sec.icon || 'bi-gear'}`}></i>
                      <span className="pm-tab-label">{sec.section}</span>
                    </div>
                    <div className="pm-tab-right">
                      {statusIcon}
                      {sec.section !== 'Basic Info' && (
                        <label className="switch" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={isActive} onChange={() => toggleSectionStatus(sec.id)} />
                          <span className={`slider ${isActive ? 'active-blue' : 'active-red'}`}></span>
                        </label>
                      )}
                      {sec.section === 'Basic Info' && (
                        <label className="switch" style={{ opacity: 0.5 }}>
                          <input type="checkbox" disabled checked />
                          <span className="slider active-blue"></span>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pm-content">
              <h3 className="pm-section-title">{sections.find((s) => s.id === activeTab)?.section}</h3>
              {renderSectionContent()}
            </div>
          </div>
          <div className="form-actions-bottom" style={{ marginTop: '20px' }}>
            <button className="btn-cancel" onClick={() => setView('list')}>
              Cancel
            </button>
            <button className="btn-submit active" onClick={handleSave}>
              Submit
            </button>
          </div>

          {/* Manage Attributes Modal */}
          {showManageModal &&
            managingGroup &&
            (() => {
              const attr = allAttributes.find((a) => String(a.id) === String(managingGroup.attr_id));
              return (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                  }}
                >
                  <div
                    style={{
                      background: '#151515',
                      border: '1px solid rgba(196, 168, 130, 0.25)',
                      borderRadius: '12px',
                      width: '450px',
                      maxWidth: '90%',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Modal Header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '15px 20px',
                        borderBottom: '1px solid rgba(196, 168, 130, 0.15)'
                      }}
                    >
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#c4a882', margin: 0 }}>Manage Attributes</h3>
                      <button
                        type="button"
                        onClick={() => {
                          setShowManageModal(false);
                          setManagingGroup(null);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(245, 240, 232, 0.6)',
                          cursor: 'pointer',
                          fontSize: '20px',
                          padding: 0
                        }}
                      >
                        &times;
                      </button>
                    </div>

                    {/* Modal Body */}
                    <div style={{ padding: '20px', maxHeight: '350px', overflowY: 'auto' }}>
                      {managingGroup.values.map((valId, idx) => {
                        const valItem = attr
                          ? (attr.value || []).find((v, i) => String(v.id !== undefined ? v.id : i) === String(valId))
                          : null;
                        if (!valItem) return null;

                        return (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 15px',
                              border: '1px solid rgba(196, 168, 130, 0.15)',
                              borderRadius: '8px',
                              marginBottom: '10px',
                              background: 'rgba(255,255,255,0.01)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              {/* Image preview */}
                              {isColorAttr(attr, valItem) && valItem.value && (
                                <div
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '4px',
                                    background: valItem.value,
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    flexShrink: 0
                                  }}
                                />
                              )}
                              {isImageAttr(attr, valItem) && valItem.value && (
                                <img
                                  src={getImageUrl(valItem.value)}
                                  alt="preview"
                                  style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                                  onError={(e) => handleImageError(e, valItem.value)}
                                />
                              )}
                              <span style={{ fontSize: '13px', color: 'rgba(245, 240, 232, 0.9)' }}>{valItem.label || valItem.value}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveValueFromGroup(valId)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
        </>
      )}

      {/* EDIT CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={!!editProductTarget}
        type="edit"
        title="Modify Product"
        itemName={editProductTarget?.product}
        itemType="product"
        onConfirm={handleConfirmEditProduct}
        onCancel={() => setEditProductTarget(null)}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={!!deleteProductTarget}
        type="delete"
        title="Delete Product"
        itemName={deleteProductTarget?.product}
        itemType="product"
        loading={isDeletingProduct}
        onConfirm={handleConfirmDeleteProduct}
        onCancel={() => setDeleteProductTarget(null)}
      />
    </div>
  );
};

export default ProductManager;
